import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { UseQueryResult, useQueries, useQueryClient } from "@tanstack/react-query";

import { Box, Stack, ToggleButtonGroup, ToggleButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Dashboard } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";


import { ActivationAudienceCard, AddAudienceCard, HelpBox } from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { InsightLayout } from "@/components/layouts";
import { CompareView } from "@/components/molecules";
import { useCompareActions, useCompareContext, useDialogs } from "@/context";
import { Audience, CompareInsight, Insight } from "@/types";
import { insightQuery, useAudiences, useClient, useToken } from "@/hooks";
import { compareModeOptions, getSegmentPaletteColor } from "@/utils";


// Stable, so useQueries returns the same array until a result changes
const combineInsights = (results: UseQueryResult<{ insight: Insight }>[]) =>
  results.map((result) => ({
    loading: result.isLoading,
    insight: result.data?.insight,
    error: result.error?.message,
  }));

const ComparePage: React.FC = () => {
  const state = useCompareContext();
  const compareActions = useCompareActions();
  const theme = useTheme();
  const getToken = useToken();
  const client = useClient();
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogs();
  const { state: navState } = useLocation();

  const { audiences } = useAudiences("build");
  const maxAudiences = 2;

  const [compareMode, setCompareMode] = useState<string | null>(null);

  const compareTabOption = {
    name: "comparison",
    label: "Compare",
    icon: <Dashboard fontSize="small" />,
    permission: "",
    description: "",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
    showDownload: false,
  }

  useEffect(() => {
    if (navState?.insight && navState?.audience) {
      // User has navigated here from the insight page, so its insight can be reused
      const { queryKey } = insightQuery(getToken, navState.audience.audience_id, client);
      if (!queryClient.getQueryData(queryKey)) {
        queryClient.setQueryData(queryKey, { audience: navState.audience, insight: navState.insight });
      }
      compareActions.set([navState.audience]);
    } else if (navState?.audience) {
      // User has navigated here from audience page, add audience to state
      compareActions.add(navState.audience);
    }

    return () => {
      // Reset the state when leaving the page
      compareActions.clear();
    }
  }, [compareActions])

  const availableAudiences = useMemo(
    () => (audiences ?? []).filter(
      (x) => !state.comparisonAudiences.some(a => a.audience_id === x.audience_id)
    ),
    [state.comparisonAudiences, audiences]
  );

  // Each audience's insight comes from the cache, so it's only fetched once
  const insightResults = useQueries({
    queries: state.comparisonAudiences.map((audience) =>
      insightQuery(getToken, audience.audience_id, client)
    ),
    combine: combineInsights,
  });

  // Memoised, as CompareView recalculates whenever its insights change
  const comparisonInsights = useMemo<CompareInsight[]>(
    () => state.comparisonAudiences.map((audience, idx) => ({
      audience_id: audience.audience_id,
      loading: insightResults[idx]?.loading ?? true,
      insight: insightResults[idx]?.insight,
    })),
    [state.comparisonAudiences, insightResults]
  );

  const compareError = insightResults.find((result) => result.error)?.error ?? null;

  const handleAddAudiences = (audiences: Audience[]) => {
    // Add the audiences to the context
    audiences.forEach((audience) => compareActions.add(audience));

    // Close the dialog
    closeDialog();
  }

  const handleRemoveAudience = (audience: Audience) => {
    // Remove the selected audience from the context
    compareActions.remove(audience);
  };

  const handleOpenSelectDialog = () => {
    openDialog({
      type: 'selectAudience',
      props: {
        audiences: availableAudiences,
        onConfirm: handleAddAudiences,
        maxAudiences: maxAudiences - state.comparisonAudiences.length,
        message: `Select the audiences you wish to compare. You can select a maximum of ${maxAudiences}.`
      },
    });
  };

  const handleCompareModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: string | null
  ) => {
    setCompareMode(value);
  };

  const compareModeToggle = (
    <Stack>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="flex-end"
        mr={0.5}
      >
        <Typography variant="caption" sx={{ mr: 1 }}>
          Mode:
        </Typography>
        <Typography variant="caption" color="secondary">
          {compareMode ?? 'All'}
        </Typography>
      </Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={compareMode}
        onChange={handleCompareModeChange}
      >
        {compareModeOptions.map((option, index) => (
          <HelpBox
            key={`compare-toggle-${index}`}
            title={option.description}
          >
            <ToggleButton value={option.value}>
              {option.icon}
            </ToggleButton>
          </HelpBox>
        ))}
      </ToggleButtonGroup>
    </Stack>
  )

  return (
    <PageContainer errors={{ ComparisonInsightsNotFound: compareError }}>
      <Grid container spacing={2}>

        <Grid size={12}>
          <Typography variant="h6" gutterBottom>Audiences</Typography>
          <Typography variant="body2" color="textSecondary">
            Select the audiences you want to compare
          </Typography>
        </Grid>

        <Grid container size={12} spacing={1}>
          {state.comparisonAudiences.map((audience, idx) => (
            <Grid key={idx}>
              <ActivationAudienceCard
                audience={audience}
                onSelect={handleRemoveAudience}
                disabled={false}
                color={getSegmentPaletteColor(theme, idx, maxAudiences)}
              />
            </Grid>
          ))}
          {state.comparisonAudiences.length < maxAudiences && (
            <Grid>
              <AddAudienceCard
                onClick={handleOpenSelectDialog}
                title="Add Audience"
              />
            </Grid>
          )}
        </Grid>

        {comparisonInsights.length > 0 && (
          <InsightLayout
            tab={compareTabOption}
            extraSlot={comparisonInsights.length === 2 ? compareModeToggle : undefined}
          >
            <CompareView
              audiences={state.comparisonAudiences}
              insights={comparisonInsights}
              mode={compareMode}
            />
          </InsightLayout>
        )}

      </Grid>
    </PageContainer>
  );
};

export default ComparePage;
